/**
 * Single source of truth for "how active is this child right now?" — used by
 * Weekly Snapshot, Parent Summary, Intelligence Brief, and chart captions so
 * no two sections contradict each other (the "33 milestones AND hasn't started"
 * bug class).
 */

export type ActivityState = "empty" | "reengage" | "active";

export interface ActivityStateInput {
  childName: string;
  lessonsTotal: number;       // all-time lessons completed
  milestonesTotal: number;    // all-time learning milestones (>= lessonsTotal)
  quizzesTotal: number;       // all-time quizzes attempted
  lessonsLast7d: number;
  lessonsLast30d: number;
  quizzesLast30d: number;
  daysSinceLastActivity: number | null;
}

export interface ActivityStateResult {
  state: ActivityState;
  hasLessonSignal: boolean;
  hasQuizSignal: boolean;
  daysInactive: number | null;
  /** One-line snapshot copy. Plain English, never contradictory. */
  snapshotLine: string;
  /** Longer summary line for the Parent Summary card. */
  summaryLine: string;
  /** Caption for "Performance Over Time" chart. */
  chartTakeaway: string;
  /** Honest copy for ParentActionLoop "struggled" placeholder. */
  struggleEmptyLine: string;
}

const REENGAGE_THRESHOLD_DAYS = 14;

function firstName(name: string): string {
  return (name || "").split(/\s+/)[0] || name || "Your child";
}

export function deriveActivityState(input: ActivityStateInput): ActivityStateResult {
  const first = firstName(input.childName);
  const total = Math.max(input.milestonesTotal, input.lessonsTotal);
  const hasLessonSignal = input.lessonsTotal > 0 || input.milestonesTotal > 0;
  const hasQuizSignal = input.quizzesTotal > 0;
  const recentlyActive =
    input.lessonsLast7d > 0 ||
    input.quizzesLast30d > 0 ||
    (input.daysSinceLastActivity !== null && input.daysSinceLastActivity <= 6);

  // Empty: nothing exists yet.
  if (total === 0 && !hasQuizSignal) {
    return {
      state: "empty",
      hasLessonSignal: false,
      hasQuizSignal: false,
      daysInactive: input.daysSinceLastActivity,
      snapshotLine: `${first} hasn't started yet. Their first lesson will unlock parent insights.`,
      summaryLine: `Not enough learning activity yet. Once lessons and quizzes are completed, HeirWise will summarize ${first}'s progress, strengths, and next steps here.`,
      chartTakeaway: `${first}'s learning trend will appear here from their first lesson onward.`,
      struggleEmptyLine: `More activity needed before HeirWise can identify a clear struggle area.`,
    };
  }

  // Re-engage: history exists but nothing recent.
  const longInactive =
    !recentlyActive &&
    (input.daysSinceLastActivity === null ||
      input.daysSinceLastActivity >= REENGAGE_THRESHOLD_DAYS);

  if (longInactive) {
    const daysCopy =
      input.daysSinceLastActivity !== null
        ? `${input.daysSinceLastActivity} days`
        : "a while";
    return {
      state: "reengage",
      hasLessonSignal,
      hasQuizSignal,
      daysInactive: input.daysSinceLastActivity,
      snapshotLine: `${first} has completed ${total} learning milestone${total === 1 ? "" : "s"}, but hasn't been active recently. This week's focus is re-engagement.`,
      summaryLine: hasQuizSignal
        ? `${first} has completed ${total} learning milestone${total === 1 ? "" : "s"}. HeirWise has enough activity to show progress trends, but needs more recent quiz and lesson activity to sharpen strengths, review areas, and next best steps.`
        : `${first} has completed ${total} learning milestone${total === 1 ? "" : "s"}. Lesson activity is available — quiz-based insights will appear after the first quiz.`,
      chartTakeaway: `Past progress is visible, but this week's activity is currently low. Last active around ${daysCopy} ago.`,
      struggleEmptyLine: hasQuizSignal
        ? `More recent quiz activity needed to confirm a struggle area.`
        : `Lesson activity is available. A first quiz will unlock targeted struggle insights.`,
    };
  }

  // Active: there's recent activity.
  return {
    state: "active",
    hasLessonSignal,
    hasQuizSignal,
    daysInactive: input.daysSinceLastActivity,
    snapshotLine: hasQuizSignal
      ? `${first} is actively building momentum this week.`
      : `${first} is actively working through lessons. Quiz-based insights will appear after the first quiz.`,
    summaryLine: hasQuizSignal
      ? `${first} is actively learning. HeirWise is tracking lessons and quizzes — strengths and focus areas will sharpen as more activity comes in.`
      : `Lesson activity is available for ${first}. Quiz-based insights will appear after the first quiz.`,
    chartTakeaway: hasQuizSignal
      ? `Trend: ${first} is keeping momentum going. Quiz scores plot below.`
      : `Lesson activity is climbing. Quiz performance will layer in once assessments are completed.`,
    struggleEmptyLine: hasQuizSignal
      ? `Nothing flagged yet from recent quizzes — keep an eye on this card as more activity comes in.`
      : `Lesson activity is available. A first quiz will unlock targeted struggle insights.`,
  };
}

/** Confidence rubric — never says "Strong" with thin data. */
export type ConfidenceLabel =
  | "Insight building"
  | "Early signal"
  | "Promising signal"
  | "Building"
  | "Strong signal";

export function confidenceFromSample(
  sampleSize: number,
  accuracyPercent: number | null,
): { label: ConfidenceLabel; canShowStrong: boolean } {
  if (sampleSize < 3) return { label: "Insight building", canShowStrong: false };
  if (sampleSize <= 4) return { label: "Early signal", canShowStrong: false };
  if (sampleSize <= 7) return { label: "Building", canShowStrong: false };
  if (accuracyPercent !== null && accuracyPercent >= 70) {
    return { label: "Strong signal", canShowStrong: true };
  }
  return { label: "Promising signal", canShowStrong: false };
}